import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  IconButton,
  Link,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import ClearFieldButton from './ClearFieldButton';
import { searchCompanies } from '../../api/companies';
import { CompanyListItem } from '../../types/company';

interface AcquiredByCompanyCellProps {
  companyId: string;
  acquiredByCompanyId?: string;
  acquiredByCompanyName?: string;
  canEdit: boolean;
  onSave: (
    companyId: string,
    acquiredByCompanyId: string,
    acquiredByCompanyName?: string
  ) => Promise<void>;
}

const AcquiredByCompanyCell: React.FC<AcquiredByCompanyCellProps> = ({
  companyId,
  acquiredByCompanyId,
  acquiredByCompanyName,
  canEdit,
  onSave,
}) => {
  const [editing, setEditing] = useState(false);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<CompanyListItem[]>([]);
  const [selected, setSelected] = useState<CompanyListItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasValue = Boolean(acquiredByCompanyId);
  const display = acquiredByCompanyName || acquiredByCompanyId || '—';

  useEffect(() => {
    if (!editing) return;
    if (!query.trim()) {
      setOptions([]);
      return;
    }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const result = await searchCompanies(query.trim(), 15);
        setOptions(result.results.filter((item) => item.id !== companyId));
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [companyId, editing, query]);

  const startEditing = () => {
    setEditing(true);
    setError(null);
    setQuery(acquiredByCompanyName || '');
    setSelected(
      acquiredByCompanyId
        ? { id: acquiredByCompanyId, name: acquiredByCompanyName || acquiredByCompanyId }
        : null
    );
  };

  const cancelEditing = () => {
    setEditing(false);
    setQuery('');
    setSelected(null);
    setOptions([]);
    setError(null);
  };

  const handleSave = async () => {
    if (!selected?.id) {
      setError('Select an acquirer company');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(companyId, selected.id, selected.name);
      cancelEditing();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        (err as Error)?.message ||
        'Failed to save';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(companyId, '', undefined);
      cancelEditing();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        (err as Error)?.message ||
        'Failed to save';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <Box sx={{ mb: 0.5 }} onClick={(e) => e.stopPropagation()}>
        <Typography variant="body2" component="div" sx={{ mb: 0.25 }}>
          <strong>Acquired by:</strong>
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
          <Autocomplete
            sx={{ flex: 1, minWidth: 0 }}
            options={options}
            loading={loading}
            value={selected}
            onChange={(_, value) => setSelected(value)}
            inputValue={query}
            onInputChange={(_, value) => setQuery(value)}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                placeholder="Search company…"
                error={Boolean(error)}
                helperText={error || undefined}
              />
            )}
          />
          {saving ? (
            <CircularProgress size={18} sx={{ mt: 1, mx: 0.5 }} />
          ) : (
            <Box sx={{ display: 'flex', mt: 0.5 }}>
              <Tooltip title="Save">
                <IconButton size="small" color="success" onClick={handleSave}>
                  <CheckIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Cancel">
                <IconButton size="small" onClick={cancelEditing}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  if (!canEdit) {
    return (
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        <strong>Acquired by:</strong> {display}
      </Typography>
    );
  }

  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}
      onClick={(e) => e.stopPropagation()}
    >
      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
        <strong>Acquired by:</strong> {display}
      </Typography>
      {hasValue ? (
        <>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={startEditing} disabled={saving}>
              <EditIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
          <ClearFieldButton onClear={handleClear} disabled={saving} />
        </>
      ) : (
        <Link
          component="button"
          type="button"
          underline="hover"
          onClick={startEditing}
          sx={{ fontSize: '0.8125rem', flexShrink: 0 }}
        >
          + Set acquirer
        </Link>
      )}
    </Box>
  );
};

export default AcquiredByCompanyCell;
